/**
 * Security-related Query Hooks
 * Handles session management and device management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api.js';
import { queryKeys } from '../../lib/queryKeys.js';

/**
 * Get user's active sessions
 * Stale time: 30 seconds
 */
export function useSecuritySessions(options = {}) {
    return useQuery({
        queryKey: queryKeys.securitySessions,
        queryFn: async () => {
            const response = await api.getSecuritySessions();
            return response;
        },
        staleTime: 30 * 1000, // 30 seconds
        ...options,
    });
}

/**
 * Revoke a specific session
 * Invalidates sessions query on success
 */
export function useRevokeSession(options = {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (sessionId) => {
            try {
                const response = await api.revokeSession(sessionId);
                return response;
            } catch (error) {
                // 🔒 FIX: Treat 404 as success - session already deleted is a valid outcome
                if (error?.status === 404 || error?.message?.includes('404')) {
                    return { success: true, alreadyRevoked: true };
                }
                throw error;
            }
        },
        // 🔒 FIX: Don't retry DELETE - session gone = success
        retry: false,
        onSuccess: () => {
            // Invalidate sessions to refresh the list
            queryClient.invalidateQueries({ queryKey: queryKeys.securitySessions });
        },
        ...options,
    });
}

/**
 * Revoke all sessions except current ("Sign out everywhere")
 * Invalidates sessions query on success
 */
export function useRevokeAllSessions(options = {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await api.revokeAllSessions();
            return response;
        },
        // 🔒 FIX: Don't retry DELETE operations
        retry: false,
        onSuccess: () => {
            // Invalidate sessions to refresh the list
            queryClient.invalidateQueries({ queryKey: queryKeys.securitySessions });
        },
        ...options,
    });
}

/**
 * Get user's remembered devices
 * Stale time: 30 seconds
 */
export function useSecurityDevices(options = {}) {
    return useQuery({
        queryKey: queryKeys.securityDevices,
        queryFn: async () => {
            const response = await api.getSecurityDevices();
            return response;
        },
        staleTime: 30 * 1000, // 30 seconds
        ...options,
    });
}

/**
 * Remove (block) a device
 * Invalidates devices and sessions queries on success
 */
export function useRemoveDevice(options = {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (deviceId) => {
            try {
                const response = await api.removeDevice(deviceId);
                return response;
            } catch (error) {
                // 🔒 FIX: Treat 404 as success - device already removed is a valid outcome
                if (error?.status === 404 || error?.message?.includes('404')) {
                    return { success: true, alreadyRemoved: true };
                }
                throw error;
            }
        },
        // 🔒 FIX: Don't retry DELETE operations
        retry: false,
        onSuccess: () => {
            // Invalidate devices to refresh the list
            queryClient.invalidateQueries({ queryKey: queryKeys.securityDevices });
            // Also invalidate sessions since device removal revokes its sessions
            queryClient.invalidateQueries({ queryKey: queryKeys.securitySessions });
        },
        ...options,
    });
}

export default {
    useSecuritySessions,
    useRevokeSession,
    useRevokeAllSessions,
    useSecurityDevices,
    useRemoveDevice,
};
